// src/components/support/NewTicketSheet.jsx
//
// Client form: which vessel, what kind of problem, how urgent, what happened,
// optional photos. When a vessel is chosen the server attaches a snapshot of
// its live state for the team, so the client doesn't have to describe it.

import React, { useEffect, useRef, useState } from 'react';
import { apiFetchJSON } from '../../api/apiClient';
import { MAX_IMAGES, PRIORITIES, imageToDataUrl } from './supportShared';

export default function NewTicketSheet({ onClose, onCreated }) {
  const [opts, setOpts] = useState(null);
  const [form, setForm] = useState({ site_id: '', category: '', priority: 'normal', subject: '', body: '' });
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const firstRef = useRef(null);

  useEffect(() => {
    apiFetchJSON('/api/v2/client/support/options')
      .then((o) => {
        setOpts(o);
        if (o.sites.length === 1) setForm((f) => ({ ...f, site_id: String(o.sites[0].site_id) }));
      })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => { opts && firstRef.current?.focus(); }, [opts]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && !busy && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e })); setError(null); };

  const addFiles = async (files) => {
    const room = MAX_IMAGES - images.length;
    try {
      const picked = await Promise.all(Array.from(files).slice(0, room).map((f) => imageToDataUrl(f)));
      setImages((p) => [...p, ...picked]);
    } catch (e) {
      setError(e.message);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) return setError('Choose what the problem is about.');
    if (form.subject.trim().length < 3) return setError('Add a short subject.');
    if (!form.body.trim()) return setError('Describe the problem.');
    setBusy(true);
    setError(null);
    try {
      const t = await apiFetchJSON('/api/v2/client/support/tickets', {
        method: 'POST',
        body: JSON.stringify({ ...form, site_id: form.site_id ? Number(form.site_id) : null, attachments: images }),
      });
      onCreated(t);
    } catch (err) {
      setError(err.status === 413 ? 'The photos are too large. Remove one and try again.' : err.message);
      setBusy(false);
    }
  };

  return (
    <div className="bl-drawer-backdrop bl-pay-backdrop" onClick={() => !busy && onClose()}>
      <form className="bl-pay sp-new" role="dialog" aria-modal="true" aria-labelledby="sp-new-title"
            onClick={(e) => e.stopPropagation()} onSubmit={submit} noValidate>
        <header className="bl-drawer-head">
          <h2 id="sp-new-title" className="sp-drawer-title">Open a ticket</h2>
          <button type="button" className="bl-icon-btn" onClick={onClose} disabled={busy} aria-label="Close"><i className="fas fa-times"></i></button>
        </header>

        {!opts && !error && <div className="bl-drawer-loading">Loading…</div>}

        {opts && (
          <div className="sp-new-body">
            {opts.sites.length > 0 && (
              <label className="sp-field">
                <span className="sp-label">Vessel</span>
                <select ref={firstRef} className="bl-select sp-input" value={form.site_id} onChange={set('site_id')}>
                  <option value="">Not about one vessel</option>
                  {opts.sites.map((s) => <option key={s.site_id} value={s.site_id}>{s.site_name}</option>)}
                </select>
              </label>
            )}

            <fieldset className="sp-field">
              <legend className="sp-label">What’s it about?</legend>
              <div className="sp-chips">
                {opts.categories.map((c) => (
                  <button key={c.id} type="button" className={`ft-pill ${form.category === c.id ? 'active' : ''}`}
                          aria-pressed={form.category === c.id} onClick={() => set('category')(c.id)}>{c.label}</button>
                ))}
              </div>
            </fieldset>

            <fieldset className="sp-field">
              <legend className="sp-label">How urgent?</legend>
              <div className="sp-urgency">
                {PRIORITIES.map((p) => (
                  <label key={p.id} className={`sp-urgency-opt ${form.priority === p.id ? 'is-on' : ''} ${p.id === 'urgent' ? 'is-urgent' : ''}`}>
                    <input type="radio" name="priority" value={p.id} checked={form.priority === p.id} onChange={set('priority')} />
                    <span className="sp-urgency-name">{p.label}</span>
                    <span className="sp-urgency-hint">{p.hint}</span>
                  </label>
                ))}
              </div>
              {form.priority === 'urgent' && (
                <p className="bl-note">Urgent tickets alert our on-call team straight away.</p>
              )}
            </fieldset>

            <label className="sp-field">
              <span className="sp-label">Subject</span>
              <input className="sp-input" maxLength={140} value={form.subject} onChange={set('subject')}
                     placeholder="e.g. No internet since this morning" />
            </label>

            <label className="sp-field">
              <span className="sp-label">What happened?</span>
              <textarea className="sp-textarea" rows={5} maxLength={5000} value={form.body} onChange={set('body')}
                        placeholder="When it started, what you’ve tried, and anything on the screen." />
            </label>

            <div className="sp-field">
              <span className="sp-label">Photos or screenshots <span className="bl-muted">(optional, up to {MAX_IMAGES})</span></span>
              <div className="sp-atts sp-atts-pending">
                {images.map((img, i) => (
                  <div key={i} className="sp-att">
                    <img src={img.data} alt={img.name} />
                    <button type="button" className="sp-att-remove" aria-label={`Remove ${img.name}`}
                            onClick={() => setImages((p) => p.filter((_, j) => j !== i))}><i className="fas fa-times"></i></button>
                  </div>
                ))}
                {images.length < MAX_IMAGES && (
                  <button type="button" className="sp-att sp-att-add" onClick={() => fileRef.current?.click()} aria-label="Add photo">
                    <i className="fas fa-camera"></i>
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => addFiles(e.target.files)} />
            </div>
          </div>
        )}

        {error && <div className="ft-toolbar-error sp-new-error" role="alert">{error}</div>}

        {opts && (
          <div className="bl-drawer-actions">
            <button type="button" className="bl-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="bl-btn-primary" disabled={busy}>{busy ? 'Sending…' : 'Send ticket'}</button>
          </div>
        )}
      </form>
    </div>
  );
}
